/**
 * <s-announcement-rotator> — Cycles its direct children one at a time.
 *
 * Attributes:
 *   data-interval — Seconds each message stays visible (default 5).
 *
 * Pauses while hovered or focused, and while the tab is hidden.
 * Inactive messages get aria-hidden and inert so only the visible one is read and focusable.
 */
class AnnouncementRotator extends HTMLElement {
  #index = 0;
  #timer = null;
  #paused = false;
  #ac = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#show(0);
    if (this.#items().length < 2) return;

    const pause = () => { this.#paused = true; };
    const resume = () => { this.#paused = false; };
    this.addEventListener('mouseenter', pause, opts);
    this.addEventListener('mouseleave', resume, opts);
    this.addEventListener('focusin', pause, opts);
    this.addEventListener('focusout', resume, opts);

    this.#start();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearInterval(this.#timer);
    this.#timer = null;
  }

  #items() {
    return Array.from(this.children);
  }

  #start() {
    const seconds = Number(this.dataset.interval) || 5;
    this.#timer = setInterval(() => {
      if (this.#paused || document.hidden) return;
      this.#show((this.#index + 1) % this.#items().length);
    }, seconds * 1000);
  }

  #show(index) {
    this.#index = index;
    this.#items().forEach((item, i) => {
      const active = i === index;
      item.toggleAttribute('data-active', active);
      item.toggleAttribute('inert', !active);
      if (active) {
        item.removeAttribute('aria-hidden');
      } else {
        item.setAttribute('aria-hidden', 'true');
      }
    });
  }
}

if (!customElements.get('s-announcement-rotator')) {
  customElements.define('s-announcement-rotator', AnnouncementRotator);
}
